import { IScheduledJob } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import * as React from "react";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherGroup
} from "../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { SCHEDULED_JOB_NAME_MESSAGE, SCHEDULED_JOB_NAME_PATTERN } from "./scheduled-job-name-modal";
import { isScheduledJobActive } from "./scheduled-job-row-actions";

/**
 * The job editor's top bar: the job's name — which is also the rename control and the job
 * switcher — the way back, and UPDATE.
 *
 * There is no three-dot menu here and no CANCEL button. Activate, inactivate and delete
 * live on the landing table, which the back arrow reaches in one click; CANCEL ran the
 * same close path the back arrow does, so two controls promised two behaviours and
 * delivered one.
 *
 * Presentation only — every handler belongs to the canvas, which owns the editor state.
 * The title must render INSIDE the editor's antd `Form`: the field is `name`, which
 * `setFieldsAndUpdate` seeds and the submit path reads back.
 */

/** A job runs or it does not — the one grouping the list's Status column already makes. */
const SCHEDULED_JOB_GROUPS: KuikaEntitySwitcherGroup[] = [
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" }
];

export interface IScheduledJobEditorHeaderProps {
  jobName: string;
  isNameOnEdit: boolean;
  onStartRename: () => void;
  /** Called with the validated name; the host commits it to its own state. */
  onSubmitName: (name: string) => void;
  onAbortRename: () => void;
  /** The field's uniqueness rule, owned by the host so create and rename share one. */
  nameValidator: (rule: unknown, value: string) => Promise<void>;
  /** Runs the field's rules and resolves the accepted name, or `undefined` if invalid. */
  validateName: () => Promise<string | undefined>;

  jobs: IScheduledJob[];
  currentJob?: IScheduledJob;
  onSwitchJob: (job: IScheduledJob) => void;
  onCreateNew: () => void;

  /** Leaving the editor. The bar's back control draws the only control for it. */
  onBack: () => void;
  /** The job list's own row actions, for the switcher's "…". */
  rowActions: KuikaListTableRowAction<IScheduledJob>[];
  onUpdate: () => void;
  /**
   * Where UPDATE stands (`KuikaButton` `status`): the label rolls to "Saving", then "Saved" or
   * "Try again". The editor stays open after a save, so the button is the confirmation — the
   * canvas owns the order (`EditorSaveStatus`).
   */
  updateStatus: KuikaButtonStatus;
}

export class ScheduledJobEditorHeader extends React.PureComponent<IScheduledJobEditorHeaderProps> {
  /**
   * Validation is awaited rather than read off the last result. The uniqueness rule is
   * async and the field is freshly mounted each time the pencil is clicked, so
   * `getFieldError` is empty on the first Enter and would let a duplicate through — which
   * is exactly what the pre-refactor `handleSubmitNameChange` did. An invalid name still
   * fails silently; the field's own message is the feedback.
   */
  handleSubmitName = async (): Promise<void> => {
    const name: string | undefined = await this.props.validateName();
    if (name === undefined) return;
    this.props.onSubmitName(name);
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== "Enter") return;
    // Stops the antd Form from submitting and keeps the designer's global key handlers
    // from seeing this Enter.
    e.stopPropagation();
    e.preventDefault();
    void this.handleSubmitName();
  };

  renderTitle = (): React.ReactNode => (
    <KuikaEditorTitle
      className="kuika-scheduled-job-editor__title"
      entityLabel="job"
      isRenaming={this.props.isNameOnEdit}
      // An as-yet-unnamed job has nothing to abort back to, so no cancel.
      canAbortRename={!!this.props.jobName}
      onStartRename={this.props.onStartRename}
      onConfirmRename={this.handleSubmitName}
      onAbortRename={this.props.onAbortRename}
      editTestId="editNameButton"
      confirmTestId="editSubmitButton"
      abortTestId="editCancelButton"
      identity={
        <KuikaEntitySwitcher<IScheduledJob>
          trigger="chip"
          triggerIcon={<KuikaSvg name="scheduled-jobs" type="topbar-icons" width={16} height={16} />}
          triggerLabel={this.props.jobName}
          triggerLabelTestId="scheduledJobEditorTitle"
          items={this.props.jobs}
          current={this.props.currentJob}
          onSelect={this.props.onSwitchJob}
          onCreateNew={this.props.onCreateNew}
          entityLabel="Scheduled Job"
          iconName="clock"
          triggerTestId="scheduledJobSwitcherTrigger"
          itemTestId={(job: IScheduledJob) => `${job.name.replace(/\s/g, "")}SwitcherItem`}
          groups={SCHEDULED_JOB_GROUPS}
          getItemGroup={(job: IScheduledJob): string => (isScheduledJobActive(job) ? "active" : "inactive")}
          rowActions={this.props.rowActions}
          onManageAll={this.props.onBack}
        />
      }
      renameField={
        <Form.Item
          name="name"
          className="kuika-scheduled-job-editor__title-field"
          rules={[
            { required: true, message: "Please enter a name." },
            { pattern: SCHEDULED_JOB_NAME_PATTERN, message: SCHEDULED_JOB_NAME_MESSAGE },
            { validator: this.props.nameValidator }
          ]}
        >
          <KuikaInput data-testid="editNameInput" onKeyDown={this.handleKeyDown} placeholder="Name" autoFocus />
        </Form.Item>
      }
    />
  );

  render() {
    return (
      <KuikaPageHeader
        variant="bar"
        title={this.props.jobName || "Scheduled Jobs"}
        onBack={this.props.onBack}
        backLabel="Back to Scheduled Jobs"
        // Back is the only way out of the editor now, so it carries the id the suite used
        // to resolve on the CANCEL button it replaced.
        backTestId="cancelButton"
        renderTitle={this.renderTitle}
        actions={
          <KuikaButton
            data-testid="updateButton"
            type="primary"
            size="md"
            status={this.props.updateStatus}
            onClick={this.props.onUpdate}
          >
            UPDATE
          </KuikaButton>
        }
      />
    );
  }
}
